import { PERMISSIONS, type PermissionCode } from '@solar/contracts';

import { paths } from '../../routes/paths';

export interface CustomerNavItem {
  to: string;
  label: string;
  end?: boolean;
  permission?: PermissionCode;
}

export interface CustomerNavContext {
  readonly hasRepaymentContext: boolean;
  readonly onboardingComplete: boolean;
}

/**
 * Primary customer sidebar items aligned with routed, permission-gated screens.
 *
 * Source of truth: `routes.tsx` + CUSTOMER role in `rbac.catalog.ts`.
 */
export const CUSTOMER_PRIMARY_NAV: readonly CustomerNavItem[] = [
  { to: paths.dashboard, label: 'Dashboard', end: true },
  {
    to: paths.customerOnboarding,
    label: 'Account setup',
    permission: PERMISSIONS.CUSTOMERS_READ,
  },
  {
    to: paths.customerProducts,
    label: 'Products',
    permission: PERMISSIONS.CUSTOMERS_READ,
  },
  {
    to: paths.customerApply,
    label: 'Apply',
    permission: PERMISSIONS.SUBSCRIPTIONS_READ,
  },
  {
    to: paths.customerApplications,
    label: 'Applications',
    permission: PERMISSIONS.SUBSCRIPTIONS_READ,
  },
  {
    to: paths.customerTimeline,
    label: 'My Solar Journey',
    permission: PERMISSIONS.CUSTOMERS_READ,
  },
  {
    to: paths.customerRepayments,
    label: 'Repayments',
    permission: PERMISSIONS.BILLING_READ,
  },
  {
    to: paths.customerPayments,
    label: 'Payments',
    permission: PERMISSIONS.BILLING_READ,
  },
  {
    to: paths.customerProfile,
    label: 'Profile',
    permission: PERMISSIONS.CUSTOMERS_READ,
  },
] as const;

const REPAYMENT_NAV_LABELS = new Set(['My Solar Journey', 'Repayments', 'Payments']);
const ONBOARDING_HIDDEN_LABELS = new Set([
  'Apply',
  'Applications',
  'My Solar Journey',
  'Repayments',
  'Payments',
  'Profile',
]);

export function buildCustomerNavItems(
  can: (permission: PermissionCode) => boolean,
  context: CustomerNavContext = { hasRepaymentContext: true, onboardingComplete: true },
): CustomerNavItem[] {
  return CUSTOMER_PRIMARY_NAV.filter((item) => {
    if (item.permission !== undefined && !can(item.permission)) {
      return false;
    }

    if (!context.onboardingComplete) {
      if (item.label === 'Dashboard') {
        return false;
      }

      if (ONBOARDING_HIDDEN_LABELS.has(item.label)) {
        return false;
      }

      return true;
    }

    if (item.label === 'Account setup') {
      return false;
    }

    if (!context.hasRepaymentContext && REPAYMENT_NAV_LABELS.has(item.label)) {
      return false;
    }

    return true;
  });
}
